import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { formatRupiah, formatDateID, STATUS_LABEL } from "@/lib/format";
import StatusStepper from "@/components/StatusStepper";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Mail, MessageCircle, FileSpreadsheet,
  ChevronDown, Trash2,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const MEAS_FIELDS = [
  ["lebar_ukuran", "Lebar Ukuran"],
  ["lebar_punggung", "Lebar Punggung"],
  ["lingkar_dada", "Lingkar Dada"],
  ["panjang_lengan", "Panjang Lengan"],
  ["panjang_tangan", "Panjang Tangan"],
  ["panjang_badan", "Panjang Badan"],
];

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ configured: false });

  const fetch = async () => {
    const { data } = await api.get(`/orders/${id}`);
    setOrder(data);
    setEmailTo(data.customer?.email || "");
  };

  useEffect(() => {
    fetch();
    api.get("/email/status").then(({ data }) => setEmailStatus(data));
  }, [id]);

  if (!order) return <div className="p-4 lg:p-10 text-stone-500">Memuat...</div>;

  const updateStatus = async (s) => {
    const { data } = await api.put(`/orders/${id}`, { status: s });
    setOrder(data);
    toast.success(`Status diubah menjadi ${STATUS_LABEL[s]}`);
  };

  const updatePayment = async (patch) => {
    const { data } = await api.put(`/orders/${id}`, patch);
    setOrder(data);
    toast.success("Pembayaran diperbarui");
  };

  const sendEmail = async () => {
    if (!emailTo) return toast.error("Isi email tujuan");
    setSending(true);
    try {
      await api.post("/email/send-invoice", {
        order_id: id,
        recipient_email: emailTo,
      });
      toast.success("Invoice terkirim via email");
      setEmailOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal kirim email");
    } finally {
      setSending(false);
    }
  };

  const waLink = () => {
    const phone = (order.customer?.phone || "").replace(/[^\d]/g, "");
    const msg = encodeURIComponent(
      `Halo ${order.customer?.name}, berikut invoice pesanan Anda No. ${order.order_number} ` +
      `dengan total ${formatRupiah(order.total)}. ` +
      `Cetak/Lihat Invoice: ${window.location.origin}/orders/${id}/invoice`
    );
    return phone
      ? `https://wa.me/${phone}?text=${msg}`
      : `https://wa.me/?text=${msg}`;
  };

  const deleteOrder = async () => {
    if (!window.confirm("Hapus order ini?")) return;
    await api.delete(`/orders/${id}`);
    toast.success("Order dihapus");
    nav("/orders");
  };

  return (
    <div className="p-4 lg:p-10 space-y-6 max-w-[1400px]" data-testid="order-detail-page">
      <header className="flex items-end justify-between border-b border-[#E5DCC7] pb-6 clf-rule">
        <div>
          <Link to="/orders" className="text-xs text-stone-500 hover:text-[#A63D30] flex items-center gap-1 mb-3">
            <ArrowLeft className="h-3 w-3" /> Kembali ke daftar
          </Link>
          <div className="clf-label mb-2">Order</div>
          <h1 className="text-3xl tracking-tight font-medium font-mono">{order.order_number}</h1>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="invoice-actions-btn"
                className="bg-[#16202B] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#243345] flex items-center gap-2"
              >
                Invoice <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-none border-[#D9CDB5] min-w-[200px]">
              <DropdownMenuItem asChild>
                <Link to={`/orders/${id}/invoice`} target="_blank"
                  data-testid="download-pdf-btn"
                  className="flex items-center gap-2 cursor-pointer w-full">
                  <Download className="h-4 w-4" /> Cetak Invoice (PDF)
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setEmailOpen(true)}
                disabled={!emailStatus.configured}
                data-testid="email-invoice-btn"
                className="flex items-center gap-2 cursor-pointer">
                <Mail className="h-4 w-4" /> Kirim via Email
                {!emailStatus.configured && <span className="text-[10px] text-stone-400 ml-auto">(belum aktif)</span>}
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href={waLink()} target="_blank" rel="noreferrer"
                  data-testid="whatsapp-btn"
                  className="flex items-center gap-2 cursor-pointer">
                  <MessageCircle className="h-4 w-4" /> Kirim via WhatsApp
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href={`${API}/orders/${id}/measurements.xlsx`} target="_blank" rel="noreferrer"
                  data-testid="download-xlsx-btn"
                  className="flex items-center gap-2 cursor-pointer">
                  <FileSpreadsheet className="h-4 w-4" /> Download Ukuran (XLSX)
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <button onClick={deleteOrder} data-testid="delete-order-btn"
            className="border border-[#D9CDB5] px-3 py-2.5 text-stone-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200">
            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {/* Status Stepper */}
      <section className="bg-white border border-[#E5DCC7] p-6">
        <div className="clf-label mb-4">Progress Pengerjaan</div>
        <StatusStepper status={order.status} onChange={updateStatus} />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Customer & Dates */}
        <div className="lg:col-span-1 bg-white border border-[#E5DCC7] p-6 space-y-5">
          <div>
            <div className="clf-label mb-2">Customer</div>
            <Link to={`/customers/${order.customer?.id}`}
              data-testid="link-to-customer"
              className="text-base font-medium hover:text-[#A63D30]">
              {order.customer?.name}
            </Link>
            <div className="text-xs text-stone-500 mt-1">{order.customer?.phone}</div>
            {order.customer?.email && (
              <div className="text-xs text-stone-500">{order.customer.email}</div>
            )}
            {order.customer?.address && (
              <div className="text-xs text-stone-500 mt-1">{order.customer.address}</div>
            )}
          </div>

          <div className="border-t border-[#E5DCC7] pt-4 space-y-3">
            <DateRow label="Order Masuk" value={order.tanggal_masuk} />
            <DateRow label="Mulai Proses" value={order.tanggal_proses} />
            <DateRow label="Selesai" value={order.tanggal_selesai} />
            <DateRow label="Diterima Customer" value={order.tanggal_diterima} />
            <DateRow label="Target Selesai" value={order.target_selesai} accent />
          </div>
        </div>

        {/* Items */}
        <div className="lg:col-span-2 bg-white border border-[#E5DCC7] p-6 space-y-4">
          <h2 className="text-lg font-medium">Item Pesanan</h2>
          {order.items.map((it, idx) => (
            <div key={it.id || idx} className="border border-[#E5DCC7] p-5 space-y-3" data-testid={`detail-item-${idx}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium">{it.product_type} {it.product_name && `— ${it.product_name}`}</div>
                  <div className="text-xs text-stone-500 mt-1">
                    Untuk: <span className="font-medium text-stone-700">{it.measurement?.untuk_siapa || "Umum"}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-stone-500">{it.quantity} × {formatRupiah(it.unit_price)}</div>
                  <div className="font-mono font-medium">{formatRupiah((it.quantity || 0) * (it.unit_price || 0))}</div>
                </div>
              </div>

              <div className="grid grid-cols-3 md:grid-cols-6 gap-3 pt-3 border-t border-stone-100">
                {MEAS_FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <div className="text-[9px] uppercase tracking-wider text-stone-500 mb-1">{label}</div>
                    <div className="font-mono text-sm font-medium">
                      {it.measurement?.[k] || 0} <span className="text-[10px] text-stone-400">cm</span>
                    </div>
                  </div>
                ))}
              </div>

              {it.notes && (
                <div className="text-xs text-stone-500 pt-2 border-t border-stone-100">
                  Catatan: {it.notes}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Payment */}
      <section className="bg-white border border-[#E5DCC7] p-6">
        <h2 className="text-lg font-medium mb-4">Pembayaran</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <div className="clf-label mb-2">Status</div>
            <select data-testid="detail-payment-status" value={order.payment_status}
              onChange={(e) => updatePayment({ payment_status: e.target.value })}
              className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900">
              <option value="belum">Belum Bayar</option>
              <option value="dp">DP</option>
              <option value="lunas">Lunas</option>
            </select>
          </div>
          <div>
            <div className="clf-label mb-2">Metode</div>
            <select data-testid="detail-payment-method" value={order.payment_method}
              onChange={(e) => updatePayment({ payment_method: e.target.value })}
              className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900">
              <option value="-">—</option>
              <option value="cash">Cash</option>
              <option value="transfer">Transfer</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <div className="clf-label mb-2">Ringkasan</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-stone-600">Subtotal</span><span className="font-mono">{formatRupiah(order.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-stone-600">Diskon</span><span className="font-mono">- {formatRupiah(order.discount)}</span></div>
              <div className="flex justify-between font-medium border-t border-[#E5DCC7] pt-1"><span>Total</span><span className="font-mono">{formatRupiah(order.total)}</span></div>
              <div className="flex justify-between text-stone-600"><span>Dibayar</span><span className="font-mono">{formatRupiah(order.total - order.balance)}</span></div>
              <div className="flex justify-between font-medium text-[#A63D30]"><span>Sisa</span><span className="font-mono">{formatRupiah(order.balance)}</span></div>
            </div>
          </div>
        </div>
      </section>

      {order.notes && (
        <section className="bg-white border border-[#E5DCC7] p-6">
          <div className="clf-label mb-2">Catatan</div>
          <p className="text-sm text-stone-700 whitespace-pre-line">{order.notes}</p>
        </section>
      )}

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="rounded-none border-[#D9CDB5]">
          <DialogHeader>
            <DialogTitle>Kirim Invoice via Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="clf-label block mb-1">Email Tujuan</label>
              <input data-testid="email-recipient-input" value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)} type="email"
                className="w-full px-3 py-2 text-sm bg-white border border-[#D9CDB5] rounded-sm focus:outline-none focus:border-stone-900" />
            </div>
            <p className="text-xs text-stone-500">
              Lampiran PDF invoice akan otomatis disertakan.
            </p>
          </div>
          <DialogFooter>
            <button onClick={() => setEmailOpen(false)} className="px-4 py-2 text-sm border border-[#D9CDB5] hover:bg-stone-100">Batal</button>
            <button onClick={sendEmail} disabled={sending} data-testid="email-send-btn"
              className="bg-[#16202B] text-white px-4 py-2 text-sm hover:bg-[#243345] disabled:opacity-50">
              {sending ? "Mengirim..." : "Kirim Email"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DateRow({ label, value, accent }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-stone-500">{label}</span>
      <span className={`font-mono ${accent ? "text-[#A63D30] font-medium" : "text-stone-800"}`}>
        {value ? formatDateID(value) : "—"}
      </span>
    </div>
  );
}
